import * as Linking from 'expo-linking';
import { IToastProps, Toast, useToast } from 'native-base';
import QueryString from 'query-string';
import { useCallback, useEffect } from 'react';
import { Platform } from 'react-native';

import { Locale } from 'app/generated/hygraph';
import useGuestDataQuery from 'app/hooks/useGuestDataQuery';
import useStore, { AuthMode, VerificationState } from 'app/hooks/useStore';
import * as navigation from 'app/navigation/navigationHelper';
import { VerificationMessageContent } from 'app/providers/AuthProvider/AuthProvider.types';
import { useI18n } from 'app/providers/I18nProvider';
import Analytics, { Events } from 'app/services/Analytics';
import AuthService from 'app/services/AuthService';
import { getAuthTokenValidation, isEmailValid } from 'app/services/GuestCenterService';
import { AuthTokenActionCode } from 'app/services/GuestCenterService.types';
import { TranslationHelper } from 'app/services/I18n';
import Logger from 'app/services/Logger';
import { sleep } from 'app/utils/time';

const MODULE = '[useLinkHandling]';

/**
 * Handles incoming or direct web links and routes them to the
 * correct handler.
 *
 * IMPORTANT: only should be used once in <Main/>
 */
export default function useLinkHandling() {
  const { t, setLocale, locale } = useI18n();
  const { setMessage } = useStore((s) => ({
    setMessage: s.setVerificationMessage,
  }));
  const { remove: clearUserData, refetch: userDataRefetch } = useGuestDataQuery();
  const raiseToast = useCallback((props: IToastProps, suppressAlerts?: boolean) => {
    if (!suppressAlerts) Toast.show(props);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const linkHandler = async (link: { url: string }, type: 'background' | 'foreground') => {
      Logger.info(`[useLinkHandling] received ${type} link`, { link });

      if (link?.url) {
        // IMPORTANT needed for ios foreground links, will review in subsequent expo sdk versions
        await sleep(100);

        routeLink({
          link,
          toast: Toast,
          raiseToast,
          clearUserData,
          locale,
          setLocale,
          setMessage,
          userDataRefetch,
          t,
        });
      }
    };

    if (Platform.OS === 'web') {
      linkHandler({ url: window.location.href }, 'foreground');
      return undefined;
    }

    const subscription = Linking.addEventListener('url', (link) => {
      Logger.debug('[useLinkHandling] foreground link', link);
      linkHandler(link, 'foreground');
    });

    Linking.getInitialURL().then((url) => {
      if (!url) {
        Logger.debug('[useLinkHandling] getInitialURL() no initial URL');
        return;
      }
      Logger.debug('[useLinkHandling] getInitialURL()', { url });
      linkHandler({ url }, 'background');
    });

    return () => {
      Logger.debug('[useLinkHandling] unsubscribe background handler');
      subscription.remove();
    };

    // only want this to fire ONCE on mount
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
}

type HandleLinkRoutingProps = {
  link: { url: string };
  suppressAlerts?: boolean;
  goToAuthScreen?: boolean;
  toast: ReturnType<typeof useToast>;
  raiseToast: (p: IToastProps, suppressAlerts?: boolean) => void;
  clearUserData: () => void;
  locale: Locale;
  setLocale: (locale: Locale) => void;
  setMessage: (message: VerificationMessageContent) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  userDataRefetch: () => Promise<any>;
  t: TranslationHelper;
};

export function routeLink({
  link,
  suppressAlerts = false,
  goToAuthScreen = true,
  toast,
  raiseToast,
  clearUserData,
  locale,
  setLocale,
  setMessage,
  userDataRefetch,
  t,
}: HandleLinkRoutingProps) {
  const url = typeof link === 'string' ? link : link.url;
  const { query, fragmentIdentifier } = QueryString.parseUrl(url, {
    parseFragmentIdentifier: true,
  });
  const hash = QueryString.parse(fragmentIdentifier ?? '');
  const { mode, continueUrl, lang, tpe, isModification } = query;
  const { email, orderAuthToken, emailReservingFor } = useStore.getState();
  const orderToken = (hash.order_token || query.order_token || '').toString();

  Logger.info(`${MODULE} handling link routing`, {
    url,
    query,
    hash,
    email,
    continueUrl,
    lang,
    isModification,
  });

  let urlEmail = tpe as string;
  if (!urlEmail && continueUrl) {
    const decoded = decodeURI(continueUrl as string);
    const { query: continueQuery } = QueryString.parseUrl(decoded);
    const { tpe: continueTpe } = continueQuery;
    if (continueTpe) {
      urlEmail = continueTpe as string;
    }
  }

  if (lang) {
    try {
      const userLang = lang as Locale;
      if (locale === Locale.En) {
        // only set when we are running in English
        setLocale(userLang);
      }
    } catch {
      Logger.info(`${MODULE} unable to set language to ${lang}`);
    }
  }

  if (mode === 'signIn') {
    Logger.info(`${MODULE} sign in flow triggered`);
    handleSignInUser({
      signInUrl: url,
      email,
      urlEmail,
      emailReservingFor,
      orderAuthToken,
      suppressAlerts,
      setMessage,
      raiseToast,
      clearUserData,
      t,
    });
  } else if (orderToken) {
    Logger.info(`${MODULE} token flow triggered`);
    try {
      handleTokenValidation({
        orderToken,
        verifiedEmail: email,
        suppressAlerts,
        goToAuthScreen,
        toast,
        raiseToast,
        clearUserData,
        userDataRefetch,
        t,
        query,
      });
    } catch (error) {
      // TODO: This is for testing purposes, we might keep it without the language translations, just english
      Logger.error(`${MODULE} error handling token validation`, { error });
      raiseToast({
        title: 'HandleTokenValidation Error',
        description: `Error handling token validation: ${error}`,
        duration: null,
        bgColor: 'red.600',
        placement: 'top',
      });
    }
  }
}

export async function handleOrderAuthToken(
  orderAuthToken: Maybe<string>,
  email: Maybe<string>,
  clearUserData: () => void
) {
  const { setAuthMode, setOrderAuthToken } = useStore.getState();

  if (orderAuthToken && email) {
    await getAuthTokenValidation(orderAuthToken, email).then((tokenValidation) => {
      if (tokenValidation?.data?.success) {
        setAuthMode(AuthMode.Unset);
        setOrderAuthToken(null);
        clearUserData();
      }
    });
  } else {
    setAuthMode(AuthMode.Unset);
    clearUserData();
  }
}

async function handleTokenValidation({
  orderToken,
  verifiedEmail,
  suppressAlerts = false,
  goToAuthScreen = true,
  toast,
  raiseToast,
  clearUserData,
  userDataRefetch,
  t,
  query,
}: {
  orderToken: string;
  verifiedEmail: Maybe<string>;
  t: TranslationHelper;
  query: QueryString.ParsedQuery<string>;
} & Pick<
  HandleLinkRoutingProps,
  'toast' | 'raiseToast' | 'clearUserData' | 'userDataRefetch' | 'suppressAlerts' | 'goToAuthScreen'
>) {
  const {
    setEmail,
    setVerificationState,
    setVerificationProcessing,
    setAuthMode,
    setEntitlementKey,
    setOrderAuthToken,
    setProduct,
  } = useStore.getState();
  const toastId = `auth_message_toast${Date.now()}`;
  let validationEmail: Maybe<string> = null;

  navigation.navigate('Authentication', undefined, { pop: true });

  setVerificationProcessing(true);
  setOrderAuthToken(orderToken);
  Logger.debug(`${MODULE} starting token validation`, { orderToken, verifiedEmail });

  try {
    validationEmail = await resolveOrderTokenValidationEmail();
    const tokenValidation = await getAuthTokenValidation(orderToken, validationEmail);
    if (tokenValidation?.data?.success) {
      const { data } = tokenValidation;
      toast.close(toastId);
      if (data.actionCode === AuthTokenActionCode.ReserveOnBehalfOf) {
        setEntitlementKey(data.locator);
        setProduct(null);
        setEmail(data.email);
        setVerificationState(VerificationState.Verified);

        // Adding timeout as an attempt to fix the following Issue: https://app.asana.com/1/822642940815154/project/1210454275349968/task/1209384807930839
        // eslint-disable-next-line no-promise-executor-return
        await new Promise((resolve) => setTimeout(resolve, 0));
        clearUserData();
        raiseToast(
          {
            title: t('app_sign_in_retrieving_data_title'),
            description: t('app_sign_in_retrieving_description', {
              guestEmail: data.email,
            }),
          },
          suppressAlerts
        );

        const refetchStartTime = Date.now();
        Logger.info(
          `${MODULE} PerformanceLog: userDataRefetch started at: ${new Date(
            refetchStartTime
          ).toISOString()}`
        );

        const result = await userDataRefetch();

        const refetchEndTime = Date.now();
        const refetchDuration = refetchEndTime - refetchStartTime;
        Logger.info(
          `${MODULE} PerformanceLog: userDataRefetch finished at: ${new Date(
            refetchEndTime
          ).toISOString()} (duration: ${refetchDuration}ms)`
        );

        if (result?.isSuccess) {
          raiseToast(
            {
              title: t('app_sign_in_retrieved_title'),
              description: t('app_sign_in_retrieved_description', {
                cityPassEmail: data.cityPassEmail ?? '',
                guestEmail: data.email,
              }),
            },
            suppressAlerts
          );

          // Adding timeout as an attempt to fix the following Issue: https://app.asana.com/1/822642940815154/project/1210454275349968/task/1209384807930839
          // eslint-disable-next-line no-promise-executor-return
          await new Promise((resolve) => setTimeout(resolve, 100));
          // on web this can finish before the navigator mounts, navigating then is a no-op
          await navigation.waitForNavigationReady();
          const { isModification } = query;
          if (!isModification) {
            // head to the auth flow to show all entitlements for the guest
            navigation.navigate('Authentication', undefined, { pop: true });
          } else {
            // route into the reservation flow with the parameters we were passed so customer care can modify a specific reservation
            const { attractionKey, productKey } = query;
            navigation.navigate('ReservationFlow', {
              attractionKey: attractionKey ?? '',
              productKey: productKey ?? '',
              isModification: true,
            });
          }
        }
      } else if (data.actionCode === AuthTokenActionCode.SignIn) {
        if (!data.token) return;

        // Sign out the old user
        if (AuthService.getCurrentUser()) {
          const old = AuthService.getCurrentUser()?.email;
          await AuthService.signOut();
          Logger.debug('signed out old user', {
            title: t('app_sign_out_title'),
            description: t('app_sign_out_description', {
              email: old ?? '',
            }),
          });
          clearUserData();
        }
        // sign in the new user
        await AuthService.signIn(data.token)
          .then(() => {
            setEmail(AuthService.getCurrentUser()?.email ?? null);
            Analytics.trackEvent(Events.AuthEmailVerified);
            setVerificationState(VerificationState.Verified);
            Logger.debug('signed in new user', {
              title: t('app_sign_in_title'),
              description: t('app_sign_in_description', {
                email: AuthService.getCurrentUser()?.email ?? '',
              }),
            });
          })
          .then(() => {
            // stay on welcome screen until user chooses to advance
            setEntitlementKey(data.locator);
            if (
              Platform.OS === 'web' ||
              (useStore.getState().hasCompletedWelcome && goToAuthScreen)
            ) {
              userDataRefetch();
            }
          })
          .catch((reason: unknown) => {
            Logger.warn(`${MODULE} failed sign in`, { reason });
            raiseToast(
              {
                title: t('app_sign_in_failed_title'),
                description: `${reason}...`,
              },
              suppressAlerts
            );
            Analytics.trackEvent(Events.AuthEmailVerificationFailed);
          })
          .finally(() => {
            toast.close(toastId);
          });
      } else if (data.actionCode === AuthTokenActionCode.VerifyEmail) {
        // put the user through the auth flow to gather / verify the email sign in flow
        // will then need to update email on the server side at the end and that can happen
        // in both the verify email flow (handleVerifyEmail) or the sign in flow (handleSignInUser)
        // depending on if the user is new or already exists
        clearUserData();
        setVerificationState(VerificationState.Unverified);
        setAuthMode(AuthMode.TokenVerifyEmail);
        toast.close(toastId);
        setVerificationProcessing(false);
        navigation.navigate(
          'Authentication',
          { screen: 'AuthenticationStepEnterEmail', pop: true },
          { pop: true }
        );
      }
    } else {
      Logger.warn(`${MODULE} token validation failed`, { data: tokenValidation?.data });
      setOrderAuthToken(null);
      // TODO: might not need to translate this toast
      raiseToast(
        {
          title: 'Token validation failed',
          description: 'Error validating token, please try again later.',
          duration: 5000,
          bgColor: 'red.600',
          placement: 'top',
        },
        suppressAlerts
      );
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    setOrderAuthToken(null);
    if (orderToken) {
      Logger.error(`${MODULE} failed to reserve on behalf of`, { error });
      // TODO: Same here with the translation, this is for CityPASS users
      raiseToast(
        {
          title: 'Invalid Token or invalid Email',
          description: `Invalid email address: ${validationEmail || 'No user logged in'}`,
          duration: 5000,
          bgColor: 'red.600',
          placement: 'top',
        },
        suppressAlerts
      );
    }
  } finally {
    try {
      toast.close(toastId);
      // eslint-disable-next-line no-empty
    } catch (error) {}
    Logger.debug(`${MODULE} finished token validation`);
    setVerificationProcessing(false);
  }
}

async function handleSignInUser({
  signInUrl,
  email,
  urlEmail,
  emailReservingFor,
  orderAuthToken,
  setMessage,
  clearUserData,
  t,
}: {
  signInUrl: string;
  email: Maybe<string>;
  urlEmail: Maybe<string>;
  emailReservingFor: Maybe<string>;
  orderAuthToken: Maybe<string>;
  t: TranslationHelper;
} & Pick<
  HandleLinkRoutingProps,
  'setMessage' | 'raiseToast' | 'clearUserData' | 'suppressAlerts'
>) {
  const { setAuthMode, setEmail, setVerificationState, setVerificationProcessing } =
    useStore.getState();
  Logger.info(`${MODULE} sign in email details ${email} / ${emailReservingFor}, / ${urlEmail}`);

  const isSignInLink = await AuthService.isSignInWithEmailLink(signInUrl);
  const emailFromSignInLink = isSignInLink && !email && urlEmail; // this is where we circumvent the auth model and provide the user with a one time sign in link that is good across app instances
  if (email || emailFromSignInLink) {
    const startUid = AuthService.getCurrentUser()?.uid;
    Logger.info(`${MODULE} start sign in`, { startUid, email, emailReservingFor });

    if (emailFromSignInLink) {
      setEmail(urlEmail);
      setAuthMode(AuthMode.Unset);
    }

    if (isSignInLink) {
      navigation.navigate('Authentication', undefined, { pop: true });
      Logger.info(`${MODULE} setting processing`);
      setVerificationProcessing(true);
      Logger.info(`${MODULE} set processing`);

      await AuthService.signInWithEmailLink(email ?? (urlEmail as string), signInUrl)
        .then(async (userCredentials: EmailLinkSignInResult | null) => {
          const user = await resolveSignedInUser(userCredentials);
          if (user?.emailVerified) {
            Logger.info(`${MODULE} signed in user`, { email: user.email });
            setVerificationState(VerificationState.Verified);
            Analytics.trackEvent(Events.AuthEmailVerified);
            setEmail(user.email);

            // Token Flow: close the loop on updating the email address for tokens representing a travel party that had no email set
            await handleOrderAuthToken(orderAuthToken, user.email, clearUserData);
            return;
          }

          Logger.warn(`${MODULE} sign in email link completed without a verified user`, {
            credentialUser: userCredentials?.user
              ? {
                  email: userCredentials.user.email,
                  emailVerified: userCredentials.user.emailVerified,
                }
              : null,
            authServiceUser: AuthService.getCurrentUser(),
          });
          setMessage({
            title: t('app_sign_in_failed_title'),
            description: t('app_sign_in_failed_invalid_link'),
            status: 'error',
          });
          Analytics.trackEvent(Events.AuthEmailVerificationFailed);
        })
        .catch((error: unknown) => {
          setMessage({
            title: t('app_sign_in_failed_title'),
            description: getSignInFailureErrorMessage(t, error),
            status: 'error',
          });
          Analytics.trackEvent(Events.AuthEmailVerificationFailed);
        })
        .finally(() => {
          setVerificationProcessing(false);
        });
    } else {
      setMessage({
        title: t('app_sign_in_failed_title'),
        description: t('app_sign_in_failed_invalid_link'),
        status: 'error',
      });
    }
  } else {
    // this flow could occur if a user triggers a sign in from another device when they already have an account
    // and then use the sign in link on another device to sign in, e.g. they use the app last year on their old phone, trigger a sign
    // in today on their desktop and then use the sign in link on their new phone
    setMessage({
      title: t('app_sign_in_triggered_title'),
      description: t('app_sign_in_gathering_email'),
    });

    // put them through a flow to gather the email and then sign them in afterwards
    setAuthMode(AuthMode.SignInGatherEmail);
    navigation.navigate(
      'Authentication',
      { screen: 'AuthenticationStepEnterEmail', pop: true },
      { pop: true }
    );
  }
}

type EmailLinkSignInResult = {
  user?: {
    email: string | null;
    emailVerified?: boolean;
    reload?: () => Promise<void>;
  } | null;
};

async function resolveSignedInUser(userCredentials: EmailLinkSignInResult | null) {
  return AuthService.resolveVerifiedUserFromEmailLinkSignIn(userCredentials);
}

async function resolveOrderTokenValidationEmail() {
  const currentUser = AuthService.getCurrentUser();
  if (currentUser?.emailVerified && isEmailValid(currentUser.email)) {
    return currentUser.email;
  }

  const syncedUser = await AuthService.getVerifiedCurrentUserAfterSync();
  if (syncedUser?.emailVerified && isEmailValid(syncedUser.email)) {
    return syncedUser.email;
  }

  return null;
}

function getSignInFailureErrorMessage(t: TranslationHelper, error: unknown) {
  try {
    const code =
      typeof error === 'object' &&
      error !== null &&
      'code' in error &&
      typeof error.code === 'string'
        ? error.code
        : null;

    switch (code) {
      case 'auth/invalid-email':
        return t('app_sign_in_failed_invalid_email_in_link');
      case 'auth/invalid-action-code':
        return t('app_sign_in_failed_link_already_used');
      default:
        return t('app_sign_in_failed_invalid_link');
    }
  } catch {
    return t('app_sign_in_failed_invalid_link');
  }
}
